import React, { useMemo, useState } from "react";

import {
  Box,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { UsageWindowSize } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import { BurndownWindow, UsageSummary } from "./use-org-ai-burndown";

// Fixed stacking order and colours per EntitlementBucket. Order matches the
// credit drain priority (welcome -> support -> purchased -> overage); free is
// only present in the per-user view. Colours are the validated categorical
// palette (blue / amber / teal / orange).
const BUCKET_DISPLAY: Array<{ key: string; label: string; color: string }> = [
  { key: "ENTITLEMENT_BUCKET_WELCOME", label: "Welcome", color: "#2a78d6" },
  { key: "ENTITLEMENT_BUCKET_SUPPORT", label: "Support", color: "#eda100" },
  { key: "ENTITLEMENT_BUCKET_PURCHASED", label: "Purchased", color: "#1baf7a" },
  { key: "ENTITLEMENT_BUCKET_FREE", label: "Free", color: "#1baf7a" },
  { key: "ENTITLEMENT_BUCKET_OVERAGE", label: "Overage", color: "#eb6834" },
];

const VIEW_W = 760;
const VIEW_H = 300;
const PAD = { top: 12, right: 14, bottom: 40, left: 46 };
const PLOT_W = VIEW_W - PAD.left - PAD.right;
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;

// Minimum horizontal space (in viewBox units) to reserve per x-axis label. The
// hourly view can have hundreds of windows, so we render a label only every Nth
// bar to keep them from overlapping into an unreadable smear.
const MIN_LABEL_SPACING = 42;

// niceScale picks a round axis maximum and step so the axis has ~4 ticks.
const niceScale = (max: number): { niceMax: number; ticks: number[] } => {
  if (max <= 0) return { niceMax: 1, ticks: [0, 1] };
  const raw = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step =
    (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) *
    mag;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= niceMax + 1e-9; t += step) ticks.push(Math.round(t));
  return { niceMax, ticks };
};

// Windows are formatted in UTC so the axis is stable regardless of the viewer's
// timezone.
const formatLabel = (d: Date, windowSize: UsageWindowSize): string =>
  windowSize === UsageWindowSize.HOUR
    ? `${String(d.getUTCHours()).padStart(2, "0")}:00`
    : `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;

const formatDate = (d: Date): string =>
  `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;

// Distinct value per UTC calendar day, used to detect day boundaries.
const utcDayKey = (d: Date): number =>
  d.getUTCFullYear() * 10000 + d.getUTCMonth() * 100 + d.getUTCDate();

interface AIBurndownChartProps {
  windows: BurndownWindow[];
  windowSize: UsageWindowSize;
  onWindowSizeChange: (ws: UsageWindowSize) => void;
  isLoading: boolean;
  // Range-wide totals for the queried period. When provided, the legend reports
  // these (the authoritative burndown-direct split) instead of re-summing the
  // per-window bars — so the legend stays correct even when OpenMeter returns no
  // windows for a sub-window-aligned range.
  summary?: UsageSummary;
}

const AIBurndownChart = ({
  windows,
  windowSize,
  onWindowSizeChange,
  isLoading,
  summary,
}: AIBurndownChartProps) => {
  const theme = useTheme();
  const gridColor = theme.palette.divider;
  const axisColor = theme.palette.text.secondary;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Period-wide total per bucket, shown in the legend so the operator can read
  // the split without hovering each bar. Prefer the server summary (the
  // authoritative burndown-direct split) when given; otherwise re-sum the
  // per-window bars. Keyed over every displayable bucket so a summary-only
  // bucket (usage the windowed series dropped) still surfaces.
  const hasSummary =
    !!summary && Object.values(summary.byBucket).some((v) => v > 0);

  const bucketTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const b of BUCKET_DISPLAY) {
      const v = hasSummary
        ? (summary?.byBucket[b.key] ?? 0)
        : windows.reduce((sum, w) => sum + (w.byBucket[b.key] ?? 0), 0);
      if (v > 0) totals[b.key] = v;
    }
    return totals;
  }, [hasSummary, summary, windows]);

  // Buckets to render, in fixed order — the union of what the bars carry and
  // what the summary reports, so bars always draw and summary-only totals show.
  const activeBuckets = useMemo(
    () =>
      BUCKET_DISPLAY.filter(
        (b) =>
          (bucketTotals[b.key] ?? 0) > 0 ||
          windows.some((w) => (w.byBucket[b.key] ?? 0) > 0),
      ),
    [bucketTotals, windows],
  );

  const grandTotal = useMemo(() => {
    if (hasSummary && summary) return summary.total;
    return Object.values(bucketTotals).reduce((sum, v) => sum + v, 0);
  }, [hasSummary, summary, bucketTotals]);

  // Non-zero buckets for a single window, in stacking order — shared by the
  // tooltip breakdown and its accessible-name fallback.
  const activeEntriesFor = (w: BurndownWindow) =>
    activeBuckets
      .map((b) => ({ ...b, value: w.byBucket[b.key] ?? 0 }))
      .filter((b) => b.value > 0);

  // Sum of the buckets actually drawn on the stack. Used (rather than
  // w.total) for the stack's pixel geometry and its "Total" row, so the
  // highlight/hover target and the reported total can never diverge from what
  // the bars visually show, even if a future bucket key is added to the API
  // response before it's added to BUCKET_DISPLAY.
  const displayedTotalFor = (w: BurndownWindow) =>
    activeEntriesFor(w).reduce((sum, b) => sum + b.value, 0);

  const renderBreakdown = (w: BurndownWindow) => (
    <Box sx={{ minWidth: 140 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
      >
        {formatLabel(w.start, windowSize)}
      </Typography>
      {activeEntriesFor(w).map((b) => (
        <Box
          key={b.key}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "2px",
                backgroundColor: b.color,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption">{b.label}</Typography>
          </Box>
          <Typography variant="caption">{b.value.toLocaleString()}</Typography>
        </Box>
      ))}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1.5,
          mt: 0.5,
          pt: 0.5,
          borderTop: "1px solid rgba(255, 255, 255, 0.3)",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Total
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {displayedTotalFor(w).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );

  const breakdownAriaLabel = (w: BurndownWindow): string => {
    const parts = activeEntriesFor(w).map(
      (b) => `${b.label} ${b.value.toLocaleString()}`,
    );
    return `${formatLabel(w.start, windowSize)}: ${parts.join(
      ", ",
    )}, Total ${displayedTotalFor(w).toLocaleString()}`;
  };

  const { niceMax, ticks } = useMemo(
    () => niceScale(Math.max(0, ...windows.map((w) => w.total))),
    [windows],
  );

  const yOf = (v: number) => PAD.top + PLOT_H - (v / niceMax) * PLOT_H;
  const band = windows.length > 0 ? PLOT_W / windows.length : PLOT_W;
  const barW = Math.min(34, band * 0.64);

  // Render a label every `labelStride` bars so they never overlap. With 7 bars
  // this is 1 (label every bar); with a week of hourly windows it thins out.
  const labelStride =
    windows.length > 0 ? Math.max(1, Math.ceil(MIN_LABEL_SPACING / band)) : 1;

  const toggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={windowSize}
      onChange={(_, value) => value != null && onWindowSizeChange(value)}
      data-testid="burndown-resolution-toggle"
    >
      <ToggleButton value={UsageWindowSize.DAY}>Day</ToggleButton>
      <ToggleButton value={UsageWindowSize.HOUR}>Hour</ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Box data-testid="ai-burndown-chart">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: "20px", pb: 1 }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Credit burndown (
          {windowSize === UsageWindowSize.HOUR ? "hourly" : "daily"})
        </Typography>
        {toggle}
      </Box>

      <Box
        sx={{
          borderColor: theme.palette.divider,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: "5px",
          backgroundColor: theme.palette.background.default,
          margin: "0 20px 20px",
          p: 1.5,
          overflowX: "auto",
        }}
      >
        {isLoading ? (
          <LinearProgress />
        ) : windows.length === 0 && activeBuckets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No usage recorded for this period.
          </Typography>
        ) : (
          <>
            {windows.length > 0 && (
              <svg
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                width="100%"
                role="group"
                aria-label="AI credit burndown by pack type"
              >
                {ticks.map((t) => (
                  <g key={t}>
                    <line
                      x1={PAD.left}
                      y1={yOf(t)}
                      x2={VIEW_W - PAD.right}
                      y2={yOf(t)}
                      stroke={t === 0 ? axisColor : gridColor}
                      strokeWidth={1}
                    />
                    <text
                      x={PAD.left - 8}
                      y={yOf(t) + 3.5}
                      textAnchor="end"
                      fontSize={11}
                      fill={axisColor}
                    >
                      {t}
                    </text>
                  </g>
                ))}

                {windows.map((w, i) => {
                  const cx = PAD.left + band * i + band / 2;
                  const x = cx - barW / 2;
                  let acc = 0;
                  const topKey = [...activeBuckets]
                    .reverse()
                    .find((b) => (w.byBucket[b.key] ?? 0) > 0)?.key;
                  // Labels are drawn every `labelStride` bars, anchored to the
                  // right edge. prevLabeled is the labelled bar immediately to the
                  // left (null if this is the leftmost labelled bar).
                  const isLabeled =
                    (windows.length - 1 - i) % labelStride === 0;
                  const prevLabeled =
                    i - labelStride >= 0 ? i - labelStride : null;
                  const displayedTotal = displayedTotalFor(w);
                  const stackTop = yOf(displayedTotal);
                  const stackBottom = yOf(0);
                  const stackHeight = Math.max(0, stackBottom - stackTop);
                  const highlightPadX = 6;
                  const highlightPadY = 4;
                  return (
                    <g key={w.start.toISOString()}>
                      {hoveredIndex === i && displayedTotal > 0 && (
                        <rect
                          data-testid="burndown-bar-highlight"
                          x={x - highlightPadX / 2}
                          y={stackTop - highlightPadY}
                          width={barW + highlightPadX}
                          height={stackHeight + highlightPadY * 2}
                          rx={4}
                          fill={theme.palette.action.hover}
                        />
                      )}
                      {activeBuckets.map((b) => {
                        const v = w.byBucket[b.key] ?? 0;
                        if (v <= 0) return null;
                        const yTop = yOf(acc + v);
                        const h = yOf(acc) - yTop;
                        acc += v;
                        const gap = b.key === topKey ? 0 : 2;
                        return (
                          <rect
                            key={b.key}
                            x={x}
                            y={yTop}
                            width={barW}
                            height={Math.max(1, h - gap)}
                            rx={b.key === topKey ? 3 : 0}
                            fill={b.color}
                          />
                        );
                      })}
                      {displayedTotal > 0 && (
                        <Tooltip
                          title={renderBreakdown(w)}
                          arrow
                          placement="top"
                        >
                          <rect
                            data-testid={`burndown-bar-overlay-${i}`}
                            x={x}
                            y={stackTop}
                            width={barW}
                            height={stackHeight}
                            fill="transparent"
                            style={{ cursor: "pointer" }}
                            tabIndex={0}
                            role="img"
                            aria-label={breakdownAriaLabel(w)}
                            onMouseEnter={() => setHoveredIndex(i)}
                            onMouseLeave={() => setHoveredIndex(null)}
                          />
                        </Tooltip>
                      )}
                      {isLabeled && (
                        <text
                          x={cx}
                          y={VIEW_H - PAD.bottom + 16}
                          textAnchor="middle"
                          fontSize={10.5}
                          fill={axisColor}
                        >
                          {formatLabel(w.start, windowSize)}
                        </text>
                      )}
                      {/* Hourly labels are just HH:00, so add the date beneath a
                        time marker whenever the UTC day changes from the
                        previous marker. Anchoring to a labelled bar keeps the
                        date and time-of-day aligned. */}
                      {isLabeled &&
                        windowSize === UsageWindowSize.HOUR &&
                        (prevLabeled == null ||
                          utcDayKey(w.start) !==
                            utcDayKey(windows[prevLabeled].start)) && (
                          <text
                            x={cx}
                            y={VIEW_H - PAD.bottom + 30}
                            textAnchor="middle"
                            fontSize={10}
                            fontWeight={600}
                            fill={axisColor}
                          >
                            {formatDate(w.start)}
                          </text>
                        )}
                    </g>
                  );
                })}
              </svg>
            )}

            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              gap={2}
              sx={{ pt: 1, pl: 1 }}
              data-testid="burndown-legend"
            >
              {activeBuckets.map((b) => (
                <Box key={b.key} display="flex" alignItems="center" gap={0.75}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "3px",
                      backgroundColor: b.color,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {b.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600 }}
                    data-testid={`burndown-legend-total-${b.key}`}
                  >
                    {(bucketTotals[b.key] ?? 0).toLocaleString()}
                  </Typography>
                </Box>
              ))}
              {activeBuckets.length > 1 && (
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600 }}
                    data-testid="burndown-legend-total"
                  >
                    {grandTotal.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AIBurndownChart;
